///
/// EarthBrowser - Dataset Data Browser (New Provider System)
///
/// Dataset browser using the new data provider system
/// Renders windows for CSV, Shapefile, and GeoJSON files
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { useCallback, useMemo, memo, useEffect, useState, useRef } from "react"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { removeOpenCSVFile, setDatasetWindowGeoPackageLayer, updateDatasetWindow, type DatasetBrowserWindowState } from "@/redux/dialogSlice"
import { setGeoPackageLayerVisible } from "@/redux/earthBrowserSlice"
import { DatasetBrowserWindow } from "@/components/windows/DatasetBrowserWindow"
import { useRegisterWindowContent } from "@/components/windows/WindowContentRegistry"
import { DataBrowserView, type DataBrowserViewHandle } from "@/components/data-browser/DataBrowserView"
import { exportToGeoPackage } from "@/components/data-browser/DataBrowserExportUtils"
import { SaveToCloudButton } from "@/components/features/user-datasets/SaveToCloudButton"
import { DataProviderRegistry } from "@/data-providers/DataProviderRegistry"
import { GeoPackageDataProvider, type GeoPackageDatabaseLike } from "@/data-providers/GeoPackageDataProvider"
import { getGeoPackageManager } from "@/earthbrowser/geopackage/GeoPackageManager"
import { FileSpreadsheet, Download, Layers, ChevronDown, Database, Globe, Map } from "lucide-react"
import type { DataRow } from "@/data-providers/types"
import { cn } from "@/utils/utils"

// Layer information for GeoPackage
interface LayerInfo {
	tableName: string
	featureCount: number
	dataType: string
	identifier?: string
	description?: string
}

// Event detail payloads dispatched by the dataset loading pipeline
interface DatasetProgressDetail {
	processedRows: number
	percentage: number
}

interface DatasetCompleteDetail {
	windowType?: string
	layers?: LayerInfo[]
	catalogId?: string
}

interface DatasetErrorDetail {
	error?: unknown
}

// Memoized single window component to prevent re-renders during drag
interface DatasetWindowRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
	onRowClick: (row: DataRow) => void
}

const DatasetWindowRenderer = memo(function DatasetWindowRenderer({
	windowId,
	windowState,
	onRowClick,
}: DatasetWindowRendererProps) {
	const dispatch = useAppDispatch()
	const [progress, setProgress] = useState<{ processedRows: number; percentage: number } | null>(null)
	const [isComplete, setIsComplete] = useState(false)
	const [layers, setLayers] = useState<LayerInfo[]>([])
	const [activeLayerIndex, setActiveLayerIndex] = useState(0)
	const [catalogId, setCatalogId] = useState<string | null>(null)
	const [showLayerMenu, setShowLayerMenu] = useState(false)
	const [showExportMenu, setShowExportMenu] = useState(false)
	const dataBrowserViewRef = useRef<DataBrowserViewHandle>(null)

	// Get visibility from Redux based on data source type
	const geopackageLayerVisibility = useAppSelector(
		state => catalogId ? state.earthbrowser.geopackageLayerVisibility?.[catalogId] ?? {} : {}
	)

	// Determine visibility based on data source type
	const activeLayer = layers[activeLayerIndex]
	const isLayerVisible = useMemo(() => {
		switch (windowState.windowType) {
			case 'csv':
			case 'shapefile':
			case 'geojson':
				// Use per-window visibility flag
				return windowState.isVisible
			case 'geopackage':
				// Use per-layer visibility for GeoPackages
				return activeLayer && catalogId ? (geopackageLayerVisibility[activeLayer.tableName] ?? true) : true
			default:
				return true
		}
	}, [windowState.windowType, windowState.isVisible, activeLayer, catalogId, geopackageLayerVisibility])

	// Listen for progress and completion events for this specific window
	useEffect(() => {
		const handleProgress = (event: CustomEvent<DatasetProgressDetail>) => {
			setProgress({
				processedRows: event.detail.processedRows,
				percentage: event.detail.percentage
			})
		}

		const handleComplete = (event: CustomEvent<DatasetCompleteDetail>) => {
			setIsComplete(true)
			setProgress(null)

			// Store layer information for GeoPackage
			if (event.detail.windowType === 'geopackage' && event.detail.layers) {
				setLayers(event.detail.layers)
				setCatalogId(event.detail.catalogId ?? null)

				// Store GeoPackage layer metadata in window state for visibility control
				if (event.detail.catalogId && event.detail.layers.length > 0) {
					dispatch(setDatasetWindowGeoPackageLayer({
						windowId,
						catalogId: event.detail.catalogId,
						activeLayerName: event.detail.layers[0].tableName
					}))
				}
			}
		}

		const handleError = (event: CustomEvent<DatasetErrorDetail>) => {
			console.error(`Error loading dataset ${windowState.title}:`, event.detail.error)
			setProgress(null)
		}

		window.addEventListener(`dataset:progress:${windowId}`, handleProgress as EventListener)
		window.addEventListener(`dataset:complete:${windowId}`, handleComplete as EventListener)
		window.addEventListener(`dataset:error:${windowId}`, handleError as EventListener)

		return () => {
			window.removeEventListener(`dataset:progress:${windowId}`, handleProgress as EventListener)
			window.removeEventListener(`dataset:complete:${windowId}`, handleComplete as EventListener)
			window.removeEventListener(`dataset:error:${windowId}`, handleError as EventListener)
		}
	}, [windowId, windowState.title, dispatch])

	// Close layer menu when clicking outside
	useEffect(() => {
		if (!showLayerMenu) return

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement
			if (!target.closest('.layer-menu-container')) {
				setShowLayerMenu(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [showLayerMenu])

	// Close export menu when clicking outside
	useEffect(() => {
		if (!showExportMenu) return

		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement
			if (!target.closest('.export-menu-container')) {
				setShowExportMenu(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [showExportMenu])

	// Handle layer change for GeoPackage
	const handleLayerChange = useCallback(async (layerIndex: number) => {
		if (layerIndex === activeLayerIndex || !catalogId || !layers[layerIndex]) {
			return
		}

		try {
			setActiveLayerIndex(layerIndex)
			setShowLayerMenu(false)

			// Get the GeoPackage database
			const gpkgManager = getGeoPackageManager()
			const db = gpkgManager.getPackage(catalogId) as GeoPackageDatabaseLike | null

			if (!db) {
				console.error(`GeoPackage database not found: ${catalogId}`)
				return
			}

			const selectedLayer = layers[layerIndex]

			// Create a new GeoPackageDataProvider for the selected layer
			const provider = new GeoPackageDataProvider({
				id: windowId,
				name: `${windowState.title} - ${selectedLayer.tableName}`,
				database: db,
				tableName: selectedLayer.tableName,
			})

			// Re-register the provider with the new layer
			DataProviderRegistry.register(windowId, provider)

			// Update window state with new active layer
			dispatch(setDatasetWindowGeoPackageLayer({
				windowId,
				catalogId,
				activeLayerName: selectedLayer.tableName
			}))

			console.log(`Switched to layer: ${selectedLayer.tableName} (${selectedLayer.featureCount} features)`)
		} catch (error) {
			console.error('Failed to switch layer:', error)
		}
	}, [activeLayerIndex, catalogId, layers, windowId, windowState.title, dispatch])

	// Toggle visibility on map based on window type
	const toggleVisibility = useCallback(() => {
		switch (windowState.windowType) {
			case 'csv':
			case 'shapefile':
			case 'geojson':
				// Toggle per-window visibility
				dispatch(updateDatasetWindow({
					id: windowId,
					updates: { isVisible: !windowState.isVisible }
				}))
				break
			case 'geopackage':
				// Toggle per-layer visibility for GeoPackages
				if (activeLayer && catalogId) {
					dispatch(setGeoPackageLayerVisible({
						packageId: catalogId,
						layerName: activeLayer.tableName,
						visible: !isLayerVisible
					}))
				}
				break
		}
	}, [dispatch, windowId, windowState.windowType, windowState.isVisible, activeLayer, catalogId, isLayerVisible])

	// Icon and color based on window type
	const { icon, iconColor } = useMemo(() => {
		switch (windowState.windowType) {
			case 'csv':
				return {
					icon: <FileSpreadsheet className="w-5 h-5" />,
					iconColor: "text-cyan-400"
				}
			case 'shapefile':
				return {
					icon: <Map className="w-5 h-5" />,
					iconColor: "text-blue-400"
				}
			case 'geojson':
				return {
					icon: <Globe className="w-5 h-5" />,
					iconColor: "text-teal-400"
				}
			case 'geopackage':
				return {
					icon: <Database className="w-5 h-5" />,
					iconColor: "text-emerald-400"
				}
			default:
				return {
					icon: <FileSpreadsheet className="w-5 h-5" />,
					iconColor: "text-cyan-400"
				}
		}
	}, [windowState.windowType])

	// Visibility toggle for titlebar - blue dot (for all window types)
	const titlebarPrefix = useMemo(() => {
		// Show for all window types
		if (!windowState.windowType) {
			return null
		}

		// For GeoPackage, wait until layer is loaded
		if (windowState.windowType === 'geopackage' && (!catalogId || !activeLayer)) {
			return null
		}

		const tooltipText = windowState.windowType === 'geopackage'
			? (isLayerVisible ? "Hide layer from map" : "Show layer on map")
			: (isLayerVisible ? "Hide data from map" : "Show data on map")

		return (
		<button
				onClick={toggleVisibility}
			className={cn(
					"flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center",
					"transition-all duration-300",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
					isLayerVisible
						? "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
						: "bg-white/5 text-white/40 hover:bg-white/10"
			)}
				aria-label={isLayerVisible ? "Hide from map" : "Show on map"}
				title={tooltipText}
		>
			<div className={cn(
					"w-2.5 h-2.5 rounded-full bg-current transition-transform duration-300",
					isLayerVisible && "scale-110"
			)} />
		</button>
		)
	}, [windowState.windowType, catalogId, activeLayer, isLayerVisible, toggleVisibility])

	// Title suffix pill badge showing data source type
	const titleSuffix = useMemo(() => {
		const typeStyles = {
			csv: {
				label: 'CSV',
				className: 'bg-cyan-500/10 text-cyan-400'
			},
			shapefile: {
				label: 'SHP',
				className: 'bg-blue-500/10 text-blue-400'
			},
			geojson: {
				label: 'JSON',
				className: 'bg-teal-500/10 text-teal-400'
			},
			geopackage: {
				label: 'GPKG',
				className: 'bg-emerald-500/10 text-emerald-400'
			}
		}

		const typeInfo = typeStyles[windowState.windowType as keyof typeof typeStyles]
		if (!typeInfo) return null

		return (
			<span
				className={cn(
					"inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold uppercase tracking-wide flex-shrink-0",
					typeInfo.className
				)}
			>
				{typeInfo.label}
			</span>
		)
	}, [windowState.windowType])

	// Handle export format selection
	const handleExport = useCallback(async (format: 'csv' | 'geojson' | 'shapefile' | 'geopackage') => {
		setShowExportMenu(false)

		if (format === 'geopackage') {
			// Get the data provider
			const provider = DataProviderRegistry.get(windowId)
			if (!provider) {
				console.error('No data provider found for export')
				return
			}

			// Use the utility function to export
			await exportToGeoPackage({
				provider,
				outputName: windowState.title,
				description: `Exported from ${windowState.title}`
			})
		} else {
			console.log(`Exporting ${windowState.title} as ${format}`)
			// TODO: Implement other export formats
		}
	}, [windowId, windowState.title])

	// Get available export formats based on data source type
	const exportFormats = useMemo(() => {
		const allFormats = [
			{ id: 'csv' as const, label: 'CSV', icon: FileSpreadsheet, description: 'Comma-separated values' },
			{ id: 'geojson' as const, label: 'GeoJSON', icon: Globe, description: 'Geographic JSON format' },
			{ id: 'shapefile' as const, label: 'Shapefile', icon: Map, description: 'ESRI Shapefile (ZIP)' },
			{ id: 'geopackage' as const, label: 'GeoPackage', icon: Database, description: 'OGC GeoPackage' },
		]

		// Filter out the current format (don't export CSV to CSV, etc.)
		return allFormats.filter(format => format.id !== windowState.windowType)
	}, [windowState.windowType])

	// Render export button with dropdown menu
	const exportMenu = useMemo(() => (
		<div className="relative export-menu-container">
			<button
				className={cn(
					"p-2 rounded-lg transition-all duration-300",
					"bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-500/50",
					"hover:scale-105 active:scale-95",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
					showExportMenu && "bg-cyan-500/20 border-cyan-500/50"
				)}
				aria-label="Export data"
				title="Export data"
				onClick={() => setShowExportMenu(!showExportMenu)}
			>
				<Download className="w-4 h-4 text-cyan-400 transition-colors duration-300" />
			</button>

			{/* Export format dropdown */}
			{showExportMenu && (
				<div className="absolute right-0 top-full mt-2 w-64 bg-black border-2 border-cyan-500/40 rounded-xl shadow-2xl z-50 overflow-hidden">
					<div className="px-4 py-2.5 border-b border-cyan-500/30 bg-gradient-to-r from-cyan-500/20 to-cyan-500/10">
						<p className="text-xs font-bold text-cyan-300 uppercase tracking-wider">Export As</p>
					</div>
					<div className="py-1 bg-black/95 backdrop-blur-xl">
						{exportFormats.map((format) => (
							<button
								key={format.id}
								onClick={() => handleExport(format.id)}
								className="w-full px-4 py-3 text-left hover:bg-cyan-500/15 transition-all duration-200 flex items-center gap-3 group border-b border-white/5 last:border-b-0"
							>
								<div className="flex-shrink-0 w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
									<format.icon className="w-4 h-4 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
								</div>
								<div className="flex-1 min-w-0">
									<div className="text-sm font-semibold text-white group-hover:text-cyan-200 transition-colors leading-tight">
										{format.label}
									</div>
									<div className="text-xs text-white/60 group-hover:text-white/80 transition-colors mt-0.5 leading-snug">
										{format.description}
									</div>
								</div>
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	), [showExportMenu, exportFormats, handleExport])

	// Layer selector for GeoPackage (rendered as actions in DataBrowserControls)
	const layerSelector = useMemo(() => {
		if (windowState.windowType !== 'geopackage') {
			return null
		}

		const currentActiveLayer = layers[activeLayerIndex]

		// Only show if multiple layers
		if (layers.length <= 1) {
			return null
		}

		return (
			<div className="relative layer-menu-container">
				<button
					onClick={() => setShowLayerMenu(!showLayerMenu)}
					className="px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 transition-colors flex items-center gap-2"
				>
					<Layers className="w-4 h-4 text-cyan-400" />
					<span className="text-sm text-white/80">
						{currentActiveLayer?.tableName || 'Select Layer'}
					</span>
					<span className="text-xs text-white/40">
						({currentActiveLayer?.featureCount.toLocaleString()})
					</span>
					<ChevronDown className={cn(
						"w-4 h-4 text-white/60 transition-transform",
						showLayerMenu && "rotate-180"
					)} />
				</button>

				{/* Layer dropdown menu */}
				{showLayerMenu && (
					<div className="absolute right-0 top-full mt-1 w-64 bg-black/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto">
						{layers.map((layer, index) => (
							<button
								key={layer.tableName}
								onClick={() => handleLayerChange(index)}
								className={cn(
									"w-full px-4 py-3 text-left hover:bg-white/10 transition-colors border-b border-white/5 last:border-b-0",
									index === activeLayerIndex && "bg-cyan-500/20"
								)}
							>
								<div className="flex items-start justify-between gap-2">
									<div className="flex-1">
										<div className="text-sm text-white/90 font-medium">
											{layer.tableName}
										</div>
										{layer.description && (
											<div className="text-xs text-white/50 mt-0.5">
												{layer.description}
											</div>
										)}
									</div>
									<div className="text-xs text-cyan-400 whitespace-nowrap">
										{layer.featureCount.toLocaleString()}
									</div>
								</div>
							</button>
						))}
					</div>
				)}
			</div>
		)
	}, [windowState.windowType, layers, activeLayerIndex, showLayerMenu, handleLayerChange])

	// Actions rendered inside the browser content so they're available both
	// when floating and when docked in the bottom drawer
	const contentActions = useMemo(() => (
		<div className="flex items-center gap-2">
			{layerSelector}
			<SaveToCloudButton windowId={windowId} title={windowState.title} />
			{exportMenu}
		</div>
	), [layerSelector, exportMenu, windowId, windowState.title])

	// Memoize content to prevent re-creation
	const content = useMemo(() => {
		// Get provider from registry
		const provider = DataProviderRegistry.get(windowId)

		if (provider && isComplete) {
			return (
				<DataBrowserView
					ref={dataBrowserViewRef}
					provider={provider}
					onRowClick={onRowClick}
					searchPlaceholder={`Search ${windowState.title}...`}
					actions={contentActions}
					disableStickyHeader={showExportMenu}
				/>
			)
		}

		// Loading state with progress
		return (
			<div className="flex flex-col items-center justify-center h-full bg-black/20 backdrop-blur-sm">
				<div className="flex flex-col items-center gap-4 p-8">
					<div className="relative w-16 h-16">
						<div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full" />
						<div className="absolute inset-0 border-4 border-transparent border-t-cyan-500 rounded-full animate-spin" />
					</div>
						<div className="flex flex-col items-center gap-2">
							<p className="text-base font-medium text-white">
							Loading {windowState.title}
							</p>
						{progress ? (
							<>
							<p className="text-sm text-white/60">
									{progress.processedRows.toLocaleString()} rows parsed
							</p>
							<div className="w-64 h-2 bg-white/10 rounded-full overflow-hidden">
								<div
									className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 transition-all duration-300"
										style={{ width: `${progress.percentage}%` }}
								/>
							</div>
							<p className="text-xs text-white/40">
									{progress.percentage.toFixed(0)}% complete
							</p>
							</>
						) : (
							<p className="text-sm text-white/60">
								Initializing...
							</p>
						)}
						</div>
				</div>
			</div>
		)
	}, [windowId, windowState.title, onRowClick, isComplete, progress, contentActions, showExportMenu])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	// Handle window being brought to front - focus search input
	const handleBringToFront = useCallback(() => {
		dataBrowserViewRef.current?.focusSearch()
	}, [])

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={icon}
			iconColor={iconColor}
			titlebarPrefix={titlebarPrefix}
			titleSuffix={titleSuffix}
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})

export function DatasetDataBrowser() {
	const dispatch = useAppDispatch()

	// Get all dataset windows from Redux
	const allDatasetWindows = useAppSelector((state) => state.dialog.datasetWindows)

	// Keep track of previous windows to detect closures
	const prevWindowsRef = useRef<Record<string, DatasetBrowserWindowState>>({})

	// Filter to only dataset windows (CSV, Shapefile, GeoJSON, GeoPackage)
	const datasetWindows = useMemo(() => {
		return Object.entries(allDatasetWindows).filter(([_, window]) => {
			return window.windowType === 'csv' ||
			       window.windowType === 'shapefile' ||
			       window.windowType === 'geojson' ||
			       window.windowType === 'geopackage'
		})
	}, [allDatasetWindows])

	// Handle row click (create stable callbacks per window)
	const handleRowClick = useCallback((windowId: string) => {
		return (row: DataRow) => {
			console.log('Dataset row clicked:', row, 'windowId:', windowId)
		}
	}, [])

	// Clean up providers and GeoPackages when windows are closed
	useEffect(() => {
		const registeredIds = DataProviderRegistry.getAllIds()
		const prevWindows = prevWindowsRef.current

		// Remove providers for windows that no longer exist
		for (const providerId of registeredIds) {
			if (!allDatasetWindows[providerId]) {
				// Get the previous window state to know what type it was
				const closedWindow = prevWindows[providerId]

				DataProviderRegistry.unregister(providerId)

				// Remove from CSV files tracking if it was a CSV
				if (closedWindow?.windowType === 'csv') {
					dispatch(removeOpenCSVFile(providerId))
				}

				// Unload GeoPackage if it was a GeoPackage window
				if (closedWindow?.windowType === 'geopackage' && closedWindow.catalogId) {
					// Dispatch event to unload the GeoPackage
					window.dispatchEvent(new CustomEvent('geopackage:unload', {
						detail: { packageId: closedWindow.catalogId }
					}))
					console.log(`Unloading GeoPackage: ${closedWindow.catalogId}`)
				}
			}
		}

		// Update the ref with current windows
		prevWindowsRef.current = { ...allDatasetWindows }
	}, [allDatasetWindows, dispatch])

	// Render all dataset windows
	return (
		<>
			{datasetWindows.map(([windowId, windowState]) => (
					<DatasetWindowRenderer
					key={windowId}
					windowId={windowId}
					windowState={windowState}
					onRowClick={handleRowClick(windowId)}
					/>
			))}
		</>
	)
}

